<template>
    <div class="DepartmentTreeChart">
        <div class="CompanyName">
            <span class="w-full font-body-md-semibold break-words" @click="$emit('clickDepartment')">
                {{ companyName }}
            </span>
            <div v-if="usingToggleChart" class="cursor-pointer" @click="$emit('clickToggle')">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6.4 19L5 17.6L10.6 12L5 6.4L6.4 5L12 10.6L17.6 5L19 6.4L13.4 12L19 17.6L17.6 19L12 13.4L6.4 19Z" fill="#666"/>
                </svg>
            </div>
        </div>

        <div class="DepartmentContent heightFix">
            <DepartmentTreeNode
                v-if="treeRoot"
                :dept="treeRoot"
                :selectedDepartmentSeq="selectedDepartmentSeq"
                :expandedSeqs="expandedSeqs"
                @clickDepartment="$emit('clickDepartment', $event)"
                @toggle="handleToggle"
            />
        </div>
    </div>
</template>

<script>
import { h } from 'vue';
import accordionOpenIcon from '@/components/design-system/icon/accordion-open.vue';

// 재귀 노드 컴포넌트 — 같은 파일 안에 render 함수로 정의
const DepartmentTreeNode = {
    name: 'DepartmentTreeNode',
    props: {
        dept: { type: Object, required: true },
        selectedDepartmentSeq: { type: String, default: '' },
        expandedSeqs: { type: Object, default: () => ({}) },
    },
    emits: ['clickDepartment', 'toggle'],
    computed: {
        hasChildren() { return !!this.dept.children?.length; },
        isExpanded() { return !!this.expandedSeqs[this.dept.departmentSeq]; },
        isSelected() { return this.selectedDepartmentSeq == this.dept.departmentSeq; },
    },
    render() {
        const { dept, selectedDepartmentSeq, expandedSeqs } = this;
        const isRoot = !dept.parentDepartmentSeq;

        const connectorEl = !isRoot
            ? h('div', { style: { width: '8px', height: '1.5px', backgroundColor: '#ECECEC', flexShrink: 0 } })
            : h('div', { style: { width: '8px', flexShrink: 0 } });

        const toggleEl = this.hasChildren
            ? h('div', {
                class: [
                    'w-8 h-8 flex items-center justify-center shrink-0',
                    'transition-transform duration-200 cursor-pointer rounded-2 hover:bg-background-basic-2',
                    { 'rotate-90': this.isExpanded },
                ],
                onClick: (e) => { e.stopPropagation(); this.$emit('toggle', dept.departmentSeq); },
              }, [h(accordionOpenIcon, { width: 16, height: 16, color: 'text-content-basic-2' })])
            : h('div', { class: 'w-8 h-8 flex items-center justify-center shrink-0' }, [
                h('div', { class: 'w-1.5 h-1.5 rounded-full bg-content-basic-4' }),
              ]);

        const nameEl = h('div', {
            class: ['DepartmentName', this.isSelected ? 'bg-primary-1' : 'bg-white hover:bg-background-basic-2'],
        }, [
            h('p', {
                class: ['font-body-sm-regular ellipsis-2 break-words', this.isSelected ? 'text-primary-3 font-semibold' : 'text-content-basic-1'],
            }, dept.departmentName),
            h('p', { class: 'font-caption-md-semibold text-content-basic-3' },
                `(${dept.ownerCount || dept.employeeCount})`),
        ]);

        const itemEl = h('div', {
            class: 'DepartmentItem flex items-center cursor-pointer h-12',
            onClick: () => this.$emit('clickDepartment', dept.departmentSeq),
        }, [connectorEl, toggleEl, nameEl]);

        const childrenEl = (this.hasChildren && this.isExpanded)
            ? h('div', { style: { marginLeft: '23px' } },
                dept.children.map((child, index) => {
                    const isLast = index === dept.children.length - 1;
                    return h('div', { key: child.departmentSeq, style: { position: 'relative' } }, [
                        h('div', {
                            style: {
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                // 마지막 자식: item 높이 절반(= h-12의 절반)까지만
                                // 나머지: 펼침 여부와 관계없이 전체 높이
                                ...(isLast ? { height: '24px' } : { bottom: 0 }),
                                width: '1px',
                                backgroundColor: '#ECECEC',
                            },
                        }),
                        h(DepartmentTreeNode, {
                            dept: child,
                            selectedDepartmentSeq,
                            expandedSeqs,
                            onClickDepartment: (seq) => this.$emit('clickDepartment', seq),
                            onToggle: (seq) => this.$emit('toggle', seq),
                        }),
                    ]);
                }),
              )
            : null;

        return h('div', [itemEl, childrenEl]);
    },
};

export default {
    components: { DepartmentTreeNode },
    props: {
        usingToggleChart: {
            type: Boolean,
            default: false,
        },
        selectedDepartmentSeq: {
            type: String,
            default: '',
        },
        menuAccessLevelCode: {
            type: String,
            default: 'LEAVE_ACCESS_LEVEL',
        },
    },
    emits: ['clickDepartment', 'clickToggle'],
    data() {
        return {
            treeRoot: null,
            companyName: '',
            expandedSeqs: {},
        };
    },
    mounted() {
        this.getDepartmentData();
        this.getCompanyName();
    },
    methods: {
        getCompanyName() {
            const url = this.$api('uri', 'get-company');
            this.$axios.get(url).then((res) => {
                this.companyName = res.data.data.companyName;
            });
        },
        handleToggle(departmentSeq) {
            this.expandedSeqs = {
                ...this.expandedSeqs,
                [departmentSeq]: !this.expandedSeqs[departmentSeq],
            };
        },
        getDepartmentData() {
            const url = this.$api('uri', 'get-department');
            const json_query = JSON.stringify({
                showRootNode: true,
                excludeEmployeeStatusList: ['RESIGNED'],
                menuAccessLevelCode: this.menuAccessLevelCode,
            });
            this.$axios
                .get(url, { params: { json_query } })
                .then((res) => {
                    const departments = res.data.data.list;
                    this.treeRoot = this.buildTree(departments);
                    this.expandedSeqs = Object.fromEntries(
                        departments.map((d) => [d.departmentSeq, true])
                    );
                })
                .catch(console.error);
        },
        buildTree(departments) {
            const map = {};
            departments.forEach((dept) => {
                map[dept.departmentSeq] = { ...dept, children: [] };
            });
            let root = null;
            const pseudoRoots = [];
            departments.forEach((dept) => {
                if (dept.parentDepartmentSeq == null) {
                    root = map[dept.departmentSeq];
                } else if (map[dept.parentDepartmentSeq]) {
                    map[dept.parentDepartmentSeq].children.push(map[dept.departmentSeq]);
                } else {
                    // 권한 필터링으로 상위 부서가 응답에서 제외된 경우, 응답에 남은 최상위 부서를 임시 루트로 표시
                    pseudoRoots.push(map[dept.departmentSeq]);
                }
            });
            return root ?? pseudoRoots[0] ?? null;
        },
    },
};
</script>
<style scoped>
</style>
